import { ReactNode, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from '@mint-lib/api';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Button,
  DialogContent,
  DialogFooter,
  Input,
  InputHelperText,
  InputLabel,
  InputRoot,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@mint-lib/tw-components';
import { DateRange } from '@mint-lib/tw-components/patterns';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import { getRouteApi } from '@mint/routing';
import { format } from 'date-fns';

import { useBudgetingStructuresApi } from '../../../../api/queries/useBudgetingStructuresApi.js';
import { useMediaPlan } from '../../../../api/queries/useMediaPlan.js';
import { YYYY_MM_DD } from '../../../../consts/index.js';
import type { Activity } from '../../../../types/activity.types.js';
import {
  CreateActivityData,
  getCreateActivityValidationSchema,
} from '../../../../utils/createActivityValidation.js';
import { ActivityDateRange } from './ActivityDateRange.jsx';
import { CurrencyInput } from './CurrencyInput.jsx';

const routerApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

type CreateActivityProps = {
  stepper: ReactNode;
  onClose: () => void;
  onCreated: (activity: Activity) => void;
};

const CREATE_ACTIVITY_FORM_ID = 'create-activity-form';

const INITIAL_DATE_RANGE: DateRange = { from: undefined };

export const CreateActivity = ({
  stepper,
  onClose,
  onCreated,
}: CreateActivityProps) => {
  const { t } = useTranslation('@myn/planning_v2');

  const nameInputId = useId();
  const budgetStructureInputId = useId();
  const budgetInputId = useId();

  const { data: budgetingStructures, isLoading: isBudgetingStructuresLoading } =
    useBudgetingStructuresApi();
  const { data: mediaPlan } = useMediaPlan();

  const selectedBudgetStructure = budgetingStructures?.[0];
  const selectedBudgetStructureValue =
    selectedBudgetStructure?.id ?? 'empty-structure';
  const selectedBudgetStructureLabel =
    selectedBudgetStructure?.levels.join(' > ') ?? t('No budget structures');

  const { control, handleSubmit, formState } = useForm<CreateActivityData>({
    defaultValues: {
      name: '',
      date: INITIAL_DATE_RANGE,
      budget: null,
    },
    resolver: zodResolver(getCreateActivityValidationSchema(t)),
  });

  const [dateRange, setDateRange] = useState<DateRange>(INITIAL_DATE_RANGE);
  const [nameValidationError, setNameValidationError] = useState('');
  const [dateRangeValidationError, setDateRangeValidationError] = useState('');

  const { campaignId: planId } = routerApi.useParams();

  const { mutateAsync: createActivity } = MediaPlan.useCreateActivity();

  const onSubmit = async (data: CreateActivityData) => {
    setNameValidationError('');
    setDateRangeValidationError('');

    try {
      const res = await createActivity({
        data: {
          mediaPlanId: planId,
          name: data.name,
          budget: data.budget === null ? null : String(data.budget),
          dateFrom: format(data.date.from, YYYY_MM_DD),
          dateTo: format(data.date.to, YYYY_MM_DD),
        },
      });
      const activity = res.data?.items?.[0];

      if (activity) {
        enqueueSnackbar({
          title: t('Activity successfully created'),
          variant: 'success',
          removable: true,
        });
        onCreated(activity);
      }
    } catch (error) {
      if (isCreateActivityValidationError(error)) {
        const validationError = error.response?.data.error;

        if (validationError?.type === 'NameValidationError') {
          setNameValidationError(validationError.message);
          return;
        }

        if (validationError?.type === 'DatesValidationError') {
          setDateRangeValidationError(validationError.message);
          return;
        }
      }

      enqueueSnackbar({
        title: t('Something went wrong'),
        subTitle: t('Failed to create activity'),
        variant: 'error',
        removable: true,
      });
    }
  };

  return (
    <>
      <DialogContent className="flex flex-col gap-6">
        {stepper}
        <form
          id={CREATE_ACTIVITY_FORM_ID}
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col gap-3"
        >
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => {
              const hasError = Boolean(fieldState.error || nameValidationError);

              return (
                <InputRoot>
                  <InputLabel label={t('Name*')} htmlFor={nameInputId} />
                  <Input
                    id={nameInputId}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('Enter name')}
                    color={hasError ? 'error' : 'default'}
                    aria-label="Enter activity name"
                  />
                  {hasError && (
                    <InputHelperText
                      color="error"
                      helperText={
                        fieldState.error?.message ?? nameValidationError
                      }
                    />
                  )}
                </InputRoot>
              );
            }}
          />
          {isBudgetingStructuresLoading ? (
            <Skeleton className="h-15" />
          ) : (
            <InputRoot>
              <InputLabel
                label={t('Budget structure*')}
                htmlFor={budgetStructureInputId}
              />
              <Select value={selectedBudgetStructureValue}>
                <SelectTrigger
                  id={budgetStructureInputId}
                  disabled
                  size="medium"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={selectedBudgetStructureValue}>
                    {selectedBudgetStructureLabel}
                  </SelectItem>
                </SelectContent>
              </Select>
            </InputRoot>
          )}
          <Controller
            control={control}
            name="date"
            render={({ field }) => {
              const dateError =
                formState.errors.date?.from ?? formState.errors.date?.to;

              return (
                <ActivityDateRange
                  control={control}
                  error={dateError}
                  dateRangeValidationError={dateRangeValidationError}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  onApply={field.onChange}
                />
              );
            }}
          />
          {mediaPlan ? (
            <Controller
              control={control}
              name="budget"
              render={({ field, fieldState }) => {
                return (
                  <InputRoot>
                    <InputLabel
                      label={t('Activity budget')}
                      htmlFor={budgetInputId}
                    />
                    <CurrencyInput
                      id={budgetInputId}
                      currency={mediaPlan.currency}
                      value={field.value}
                      onChange={field.onChange}
                      color={fieldState.error ? 'error' : 'default'}
                      aria-label="Enter activity budget"
                    />
                    {fieldState.error?.message && (
                      <InputHelperText
                        color="error"
                        helperText={fieldState.error.message}
                      />
                    )}
                  </InputRoot>
                );
              }}
            />
          ) : (
            <Skeleton className="h-15" />
          )}
        </form>
      </DialogContent>
      <DialogFooter>
        <Button
          size="large"
          variant="text"
          onClick={onClose}
          aria-label="Close create activity modal"
        >
          {t('Cancel')}
        </Button>
        <Button
          size="large"
          color="primary"
          type="submit"
          form={CREATE_ACTIVITY_FORM_ID}
          disabled={!formState.isDirty || formState.isSubmitting}
          aria-label="Continue to flights step"
        >
          {t('Next')}
        </Button>
      </DialogFooter>
    </>
  );
};

function isCreateActivityValidationError(
  error: unknown,
): error is AxiosError<MediaPlan.CreateActivityResponse> {
  return !!(error as AxiosError<MediaPlan.CreateActivityResponse>)?.response
    ?.data?.error;
}
